import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import PersonalIdentityCard from '@/components/profile/PersonalIdentityCard';
import ResumeUpload from "@/components/resume/ResumeUpload";
import DetailsView from "@/components/resume/detail/DetailsView";
import { EducationTab } from "@/components/resume/education/EducationTab";
import EmploymentTab from "@/components/resume/employment/EmploymentTab";
import {
    ProfileSectionCreatePanel,
    ProfileSectionEditor,
} from "@/components/resume/profile-section/ProfileSectionEditor";
import SkillsView from "@/components/resume/skill/SkillsView";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useProfile } from '@/contexts/ProfileContext';
import { ProfileSectionResponse, useApiClient } from '@/lib/api';
import { Briefcase, ChevronLeft, ChevronRight, FileText, GraduationCap, Plus, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const Me = () => {
    const { setHasProfileData } = useProfile();
    const { api } = useApiClient();
    const tabsRef = useRef<HTMLDivElement>(null);
    const [activeTab, setActiveTab] = useState('education');
    const [profileSections, setProfileSections] = useState<ProfileSectionResponse[]>([]);

    useEffect(() => {
        // Let's assume if they are on this page, they have profile data.
        // The child components now manage their own data fetching and state.
        setHasProfileData(true);
    }, [setHasProfileData]);

    useEffect(() => {
        const loadProfileSections = async () => {
            try {
                setProfileSections(await api.getProfileSections());
            } catch (error) {
                console.error('Failed to load profile sections', error);
            }
        };

        loadProfileSections();
    }, [api]);

    const scrollTabs = (direction: 'left' | 'right') => {
        tabsRef.current?.scrollBy({
            left: direction === 'left' ? -280 : 280,
            behavior: 'smooth',
        });
    };

    const sectionTabId = (sectionId: string) => `section:${sectionId}`;
    const activeProfileSection = profileSections.find(
        section => activeTab === sectionTabId(section.id)
    );

    const handleSectionCreated = (section: ProfileSectionResponse) => {
        setProfileSections(prevSections => [...prevSections, section]);
        setActiveTab(sectionTabId(section.id));
        setTimeout(() => scrollTabs('right'), 0);
    };

    const handleSectionUpdated = (section: ProfileSectionResponse) => {
        setProfileSections(prevSections => prevSections.map(currentSection => (
            currentSection.id === section.id ? section : currentSection
        )));
    };

    const handleSectionDeleted = (sectionId: string) => {
        setProfileSections(prevSections => prevSections.filter(section => section.id !== sectionId));
        if (activeTab === sectionTabId(sectionId)) {
            setActiveTab('create-section');
        }
    };

    const tabButtonClass = (tabId: string) => (
        `flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === tabId ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'
        }`
    );

    return (
        <DashboardLayout>
            <div className="-m-4 h-[calc(100vh-4rem)]">
                <Card className="w-full h-full shadow-lg flex flex-col">
                    <CardHeader className="px-3 sm:px-4 md:px-6 py-3 sm:py-4">
                        <CardTitle className="text-xl sm:text-2xl md:text-3xl font-bold">Your Profile</CardTitle>
                        <CardDescription className="text-sm sm:text-base">Keep your professional information up-to-date.</CardDescription>
                    </CardHeader>
                    <CardContent className="flex-grow overflow-y-auto px-3 sm:px-4 md:px-6">
                        <div className="mb-4 flex items-center border-b sm:mb-6">
                            <button
                                type="button"
                                className="hidden h-9 w-8 shrink-0 items-center justify-center text-gray-500 hover:text-gray-800 sm:flex"
                                onClick={() => scrollTabs('left')}
                                aria-label="Scroll tabs left"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <div
                                ref={tabsRef}
                                className="flex min-w-0 flex-1 overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                            >
                            <button
                                className={tabButtonClass('education')}
                                onClick={() => setActiveTab('education')}
                            >
                                <GraduationCap className="h-4 w-4 sm:h-5 sm:w-5" />
                                <span>Education</span>
                            </button>
                            <button
                                className={tabButtonClass('experience')}
                                onClick={() => setActiveTab('experience')}
                            >
                                <Briefcase className="h-4 w-4 sm:h-5 sm:w-5" />
                                <span>Experience</span>
                            </button>
                            <button
                                className={tabButtonClass('general')}
                                onClick={() => setActiveTab('general')}
                            >
                                <User className="h-4 w-4 sm:h-5 sm:w-5" />
                                <span>General</span>
                            </button>
                            {profileSections.map(section => (
                                <button
                                    key={section.id}
                                    className={tabButtonClass(sectionTabId(section.id))}
                                    onClick={() => setActiveTab(sectionTabId(section.id))}
                                >
                                    <User className="h-4 w-4 sm:h-5 sm:w-5" />
                                    <span>{section.title}</span>
                                </button>
                            ))}
                            <button
                                className={tabButtonClass('create-section')}
                                onClick={() => setActiveTab('create-section')}
                                aria-label="Create profile section"
                            >
                                <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                            <button
                                className={tabButtonClass('resume')}
                                onClick={() => setActiveTab('resume')}
                            >
                                <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
                                <span>Resume</span>
                            </button>
                            <button
                                className={tabButtonClass('identity')}
                                onClick={() => setActiveTab('identity')}
                            >
                                <User className="h-4 w-4 sm:h-5 sm:w-5" />
                                <span>Personal Identity</span>
                            </button>
                            </div>
                            <button
                                type="button"
                                className="hidden h-9 w-8 shrink-0 items-center justify-center text-gray-500 hover:text-gray-800 sm:flex"
                                onClick={() => scrollTabs('right')}
                                aria-label="Scroll tabs right"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                        <div className={activeTab === 'education' ? '' : 'hidden'}>
                            <div className="px-0 sm:px-2">
                                <EducationTab />
                            </div>
                        </div>
                        <div className={activeTab === 'experience' ? '' : 'hidden'}>
                            <div className="px-0 sm:px-2">
                                <EmploymentTab />
                            </div>
                        </div>
                        <div className={activeTab === 'general' ? '' : 'hidden'}>
                            <div className="w-full px-2 sm:px-4 space-y-4 sm:space-y-6">
                                <div>
                                    <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3">Key Skills</h3>
                                    <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">Add skills that are not specific to any job, like scheduling, office software, customer service, or other relevant abilities.</p>
                                    <SkillsView />
                                </div>
                                <div>
                                    <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3">General Achievements & Details</h3>
                                    <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">Include any other details or accomplishments you want to highlight that don't fit under a specific job or education entry.</p>
                                    <DetailsView />
                                </div>
                            </div>
                        </div>
                        <div className={activeTab === 'create-section' ? '' : 'hidden'}>
                            <div className="w-full px-2 sm:px-4">
                                <ProfileSectionCreatePanel
                                    existingSectionKeys={new Set(profileSections.map(section => section.section_key))}
                                    onSectionCreated={handleSectionCreated}
                                />
                            </div>
                        </div>
                        {activeProfileSection ? (
                            <div className="w-full px-2 sm:px-4">
                                <ProfileSectionEditor
                                    section={activeProfileSection}
                                    onSectionUpdated={handleSectionUpdated}
                                    onSectionDeleted={handleSectionDeleted}
                                />
                            </div>
                        ) : null}
                        <div className={activeTab === 'resume' ? '' : 'hidden'}>
                            <div className="px-0 sm:px-2">
                                <ResumeUpload onUpload={() => {
                                    window.location.reload();
                                }} />
                            </div>
                        </div>
                        <div className={activeTab === 'identity' ? '' : 'hidden'}>
                            <div className="px-0 sm:px-2">
                                <PersonalIdentityCard
                                    title="Personal Identity"
                                    description="Set the name and title information used throughout your profile, research guidance, and resume generation."
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </DashboardLayout>
    );
};

export default Me;
